import hotkeys, { type KeyHandler } from "hotkeys-js";
import { useEffect, useState } from "react";
import { canv } from "@/canv/global";
import { formatSecondsToMMSS } from "@/utils";

export const useHotkeys = (keys: string, callback: KeyHandler) => {
    useEffect(() => {
        hotkeys(keys, callback);
        return () => {
            hotkeys.unbind(keys, callback);
        };
    }, [keys, callback]);
};

type UseTimerReturn = {
    showTimer: boolean;
    isTimerOpen: boolean;
    timerTime: string;
    isTimerRunning: boolean;
    isTimerActive: boolean;
    handleTimerClick: () => void;
};

export const useTimer = (): UseTimerReturn => {
    const [isTimerOpen, setIsTimerOpen] = useState(false);
    const [timerTime, setTimerTime] = useState<string>("");
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const showTimer = !canv.isBoardModeView();

    useEffect(() => {
        // Отслеживает появление/закрытие окна таймера в DOM и выставляет isTimerOpen
        const checkTimerWindow = () => {
            const timerWindow = document.querySelector(".timerWindow");
            setIsTimerOpen(!!timerWindow);
        };

        checkTimerWindow();

        const observer = new MutationObserver(checkTimerWindow);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        // Синхронизирует время и состояние таймера с legacy DOM (timerWindow)
        // через скрытый элемент и MutationObserver
        if (!showTimer) return;

        // Создаем скрытый элемент для синхронизации с updateTimerVisuals()
        let syncElement: HTMLElement | null = null;
        const existingElement = document.querySelector(".barPanelLeftTimerTime");

        if (!existingElement) {
            syncElement = document.createElement("span");
            syncElement.className = "barPanelLeftTimerTime";
            syncElement.style.display = "none";
            syncElement.style.position = "absolute";
            syncElement.style.visibility = "hidden";
            document.body.appendChild(syncElement);
        }

        const timerElement = (existingElement as HTMLElement) || syncElement;

        const updateTimerTime = () => {
            const timerWindowInstance = canv.appInterface.timerWindow;
            if (timerWindowInstance) {
                const currentTime = timerWindowInstance.currentTime ?? 30;
                // Проверяем наличие класса 'running' на элементе синхронизации
                // Класс остается при паузе и удаляется только при остановке
                const hasRunningClass = timerElement.classList.contains("running");

                setTimerTime(formatSecondsToMMSS(currentTime));
                setIsTimerRunning(hasRunningClass);
            }
        };

        // Обновляем время сразу
        updateTimerTime();

        // Отслеживаем изменения элемента, который обновляется синхронно с окном таймера
        const observer = new MutationObserver(() => {
            // Читаем время из элемента, который обновляется updateTimerVisuals()
            if (timerElement.textContent) {
                setTimerTime(timerElement.textContent);
            }
            // Проверяем наличие класса 'running', который означает что таймер был запущен
            // (включая состояние паузы)
            const hasRunningClass = timerElement.classList.contains("running");
            setIsTimerRunning(hasRunningClass);
        });

        observer.observe(timerElement, {
            characterData: true,
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class"],
        });

        // Также проверяем статус через небольшой интервал для гарантии синхронизации
        const interval = setInterval(() => {
            updateTimerTime();
        }, 50);

        return () => {
            observer.disconnect();
            clearInterval(interval);
            if (syncElement && syncElement.parentNode) {
                syncElement.parentNode.removeChild(syncElement);
            }
        };
    }, [showTimer]);

    const handleTimerClick = () => {
        canv.appInterface.timerWindow.open();
    };

    const isTimerActive = isTimerOpen || isTimerRunning;

    return {
        showTimer,
        isTimerOpen,
        timerTime,
        isTimerRunning,
        isTimerActive,
        handleTimerClick,
    };
};
